import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Autocomplete,
  Divider,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SmartToy as SmartToyIcon,
  Build as BuildIcon,
  Storage as StorageIcon,
  Description as DescriptionIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import api from '../../config/api';
import DocumentUploader from '../documents/DocumentUploader';

const TOOL_TYPES = ['sql_query', 'template', 'api_call', 'python_script', 'mcp_proxy', 'knowledge_search'];

// Preset suggestions — admin can also type any custom value
const DEFAULT_AGENT_TYPES = [
  'general', 'analyst', 'head_of_brand', 'ecommerce_manager',
  'retention_expert', 'performance_marketing', 'operations',
  'content_seo', 'finance', 'creative_head'
];

const AIAgentsTab = () => {
  const theme = useTheme();
  const [subTab, setSubTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Brands for dropdown
  const [brands, setBrands] = useState([]);

  // Agents state
  const [agents, setAgents] = useState([]);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentForm, setAgentForm] = useState({ id: '', name: '', description: '', system_prompt: '', brand_id: 0, agent_type: 'general', personality_traits: '', gemini_store_name: '' });

  // Agent-tool assignment state
  const [toolAssignDialogOpen, setToolAssignDialogOpen] = useState(false);
  const [assigningAgent, setAssigningAgent] = useState(null);
  const [agentToolIds, setAgentToolIds] = useState([]);

  // Tools state
  const [tools, setTools] = useState([]);
  const [toolDialogOpen, setToolDialogOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [toolForm, setToolForm] = useState({ id: '', name: '', description: '', tool_type: 'sql_query', config: '{}', requires_approval: false });

  // Servers state
  const [servers, setServers] = useState([]);

  // Document upload state
  const [docBrandId, setDocBrandId] = useState(0);

  // Agent-template assignment state
  const [templateAssignDialogOpen, setTemplateAssignDialogOpen] = useState(false);
  const [templateAssigningAgent, setTemplateAssigningAgent] = useState(null);
  const [agentTemplateIds, setAgentTemplateIds] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [templateSearch, setTemplateSearch] = useState('');

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'agent'|'tool', id, name }

  useEffect(() => {
    fetchAgents();
    fetchTools();
    fetchServers();
    fetchBrands();
  }, []);

  // ---- Fetch ----
  const fetchAgents = async () => {
    try {
      const res = await api.get('/api/mcp-admin/agents');
      setAgents(res.data);
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  const fetchTools = async () => {
    try {
      const res = await api.get('/api/mcp-admin/tools');
      setTools(res.data);
    } catch (err) {
      console.error('Error fetching tools:', err);
    }
  };

  const fetchServers = async () => {
    try {
      const res = await api.get('/api/mcp-admin/servers');
      setServers(res.data);
    } catch (err) {
      console.error('Error fetching servers:', err);
    }
  };

  const fetchBrands = async () => {
    try {
      const res = await api.get('/api/chat/brands');
      setBrands(res.data || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  // ---- Agents CRUD ----
  const openAgentDialog = (agent = null) => {
    if (agent) {
      setEditingAgent(agent);
      setAgentForm({
        id: agent.id, name: agent.name, description: agent.description || '',
        system_prompt: agent.system_prompt || '', brand_id: agent.brand_id || 0,
        agent_type: agent.agent_type || 'general', personality_traits: agent.personality_traits || '',
        gemini_store_name: agent.gemini_store_name || ''
      });
      setDocBrandId(agent.brand_id || 0);
    } else {
      setEditingAgent(null);
      setAgentForm({ id: '', name: '', description: '', system_prompt: '', brand_id: 0, agent_type: 'general', personality_traits: '', gemini_store_name: '' });
      setDocBrandId(0);
    }
    setError('');
    setAgentDialogOpen(true);
  };

  const handleSaveAgent = async () => {
    if (!agentForm.id || !agentForm.name) {
      setError('ID and Name are required');
      return;
    }
    try {
      setLoading(true);
      setError('');
      if (editingAgent) {
        await api.put(`/api/mcp-admin/agents/${editingAgent.id}`, {
          name: agentForm.name,
          description: agentForm.description,
          system_prompt: agentForm.system_prompt,
          brand_id: agentForm.brand_id,
          agent_type: agentForm.agent_type,
          personality_traits: agentForm.personality_traits,
          gemini_store_name: agentForm.gemini_store_name
        });
        setSuccess(`Agent "${agentForm.name}" updated`);
      } else {
        await api.post('/api/mcp-admin/agents', agentForm);
        setSuccess(`Agent "${agentForm.name}" created`);
      }
      setAgentDialogOpen(false);
      fetchAgents();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save agent');
    } finally {
      setLoading(false);
    }
  };

  // ---- Agent-Tool Assignment ----
  const openToolAssignment = async (agent) => {
    setAssigningAgent(agent);
    try {
      const res = await api.get(`/api/mcp-admin/agents/${agent.id}/tools`);
      setAgentToolIds(res.data.map(t => t.tool_id));
    } catch (err) {
      // DT-2017 sibling: the save replaces the agent's whole tool list, so never open it on a guessed [].
      console.error('Error fetching agent tools:', err);
      setError(`Could not load the tools assigned to "${agent.name}", so they cannot be changed now. Try again.`);
      return;
    }
    setError(''); setToolAssignDialogOpen(true);
  };

  const handleToggleToolAssignment = (toolId) => {
    setAgentToolIds(prev =>
      prev.includes(toolId) ? prev.filter(id => id !== toolId) : [...prev, toolId]
    );
  };

  const handleSaveToolAssignment = async () => {
    if (!assigningAgent) return;
    try {
      setLoading(true);
      await api.put(`/api/mcp-admin/agents/${assigningAgent.id}/tools`, { tool_ids: agentToolIds });
      setSuccess(`Tools updated for "${assigningAgent.name}"`);
      setToolAssignDialogOpen(false);
      fetchAgents(); // Refresh tool counts
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save tool assignment');
    } finally {
      setLoading(false);
    }
  };

  // ---- Agent-Template Assignment ----
  const openTemplateAssignment = async (agent) => {
    setTemplateAssigningAgent(agent);
    setTemplateSearch('');
    try {
      const [tplRes, agentTplRes] = await Promise.all([
        api.get('/api/mcp-admin/templates'),
        api.get(`/api/mcp-admin/agents/${agent.id}/templates`)
      ]);
      setAllTemplates(tplRes.data);
      setAgentTemplateIds(agentTplRes.data.map(t => t.template_id));
    } catch (err) {
      // DT-2017 sibling: the save replaces the agent's whole template list, so never open it on a guessed [].
      console.error('Error fetching agent templates:', err);
      setError(`Could not load the templates for "${agent.name}", so they cannot be changed now. Try again.`);
      return;
    }
    setError(''); setTemplateAssignDialogOpen(true);
  };

  const handleToggleTemplateAssignment = (templateId) => {
    setAgentTemplateIds(prev =>
      prev.includes(templateId) ? prev.filter(id => id !== templateId) : [...prev, templateId]
    );
  };

  const handleSaveTemplateAssignment = async () => {
    if (!templateAssigningAgent) return;
    try {
      setLoading(true);
      await api.put(`/api/mcp-admin/agents/${templateAssigningAgent.id}/templates`, { template_ids: agentTemplateIds });
      setSuccess(`Templates updated for "${templateAssigningAgent.name}" (${agentTemplateIds.length} templates)`);
      setTemplateAssignDialogOpen(false);
      fetchAgents();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save template assignment');
    } finally {
      setLoading(false);
    }
  };

  // ---- Tools CRUD ----
  const openToolDialog = (tool = null) => {
    if (tool) {
      setEditingTool(tool);
      setToolForm({
        id: tool.id,
        name: tool.name,
        description: tool.description || '',
        tool_type: tool.tool_type,
        config: tool.config || '{}',
        requires_approval: !!tool.requires_approval
      });
    } else {
      setEditingTool(null);
      setToolForm({ id: '', name: '', description: '', tool_type: 'sql_query', config: '{}', requires_approval: false });
    }
    setError('');
    setToolDialogOpen(true);
  };

  const handleSaveTool = async () => {
    if (!toolForm.id || !toolForm.name || !toolForm.tool_type) {
      setError('ID, Name, and Type are required');
      return;
    }
    // Validate config JSON
    try {
      JSON.parse(toolForm.config);
    } catch (e) {
      setError('Config must be valid JSON');
      return;
    }
    try {
      setLoading(true);
      setError('');
      if (editingTool) {
        await api.put(`/api/mcp-admin/tools/${editingTool.id}`, {
          name: toolForm.name,
          description: toolForm.description,
          tool_type: toolForm.tool_type,
          config: toolForm.config,
          requires_approval: toolForm.requires_approval
        });
        setSuccess(`Tool "${toolForm.name}" updated`);
      } else {
        await api.post('/api/mcp-admin/tools', toolForm);
        setSuccess(`Tool "${toolForm.name}" created`);
      }
      setToolDialogOpen(false);
      fetchTools();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save tool');
    } finally {
      setLoading(false);
    }
  };

  // ---- Delete ----
  const confirmDelete = (type, id, name) => {
    setDeleteTarget({ type, id, name });
    setError(''); setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setLoading(true);
      setError('');
      if (deleteTarget.type === 'agent') {
        await api.delete(`/api/mcp-admin/agents/${deleteTarget.id}`);
        setSuccess(`Agent "${deleteTarget.name}" deleted`);
        fetchAgents();
      } else {
        await api.delete(`/api/mcp-admin/tools/${deleteTarget.id}`);
        setSuccess(`Tool "${deleteTarget.name}" deleted`);
        fetchTools();
      }
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete');
    } finally {
      setLoading(false);
    }
  };

  // ---- Server Toggle ----
  const handleToggleServer = async (serverId) => {
    try {
      await api.put(`/api/mcp-admin/servers/${serverId}/toggle`);
      fetchServers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to toggle server');
    }
  };

  const typeColor = (type) => {
    const colors = {
      sql_query: theme.palette.info.main,
      template: theme.palette.primary.main,
      api_call: theme.palette.warning.main,
      python_script: theme.palette.success.main,
      mcp_proxy: theme.palette.secondary.main,
      knowledge_search: '#9C27B0'
    };
    return colors[type] || theme.palette.grey[500];
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={subTab} onChange={(e, v) => setSubTab(v)}>
          <Tab label="Agents" icon={<SmartToyIcon />} iconPosition="start" />
          <Tab label="Tools" icon={<BuildIcon />} iconPosition="start" />
          <Tab label="MCP Servers" icon={<StorageIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* ---- Agents Sub-tab ---- */}
      {subTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">AI Agents</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAgentDialog()} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)' }}>
              Add Agent
            </Button>
          </Box>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tools</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Templates</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Users</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agents.map(agent => {
                  const brandName = agent.brand_id > 0
                    ? (brands.find(b => b.id === agent.brand_id)?.name || `Brand #${agent.brand_id}`)
                    : 'All Brands';
                  return (
                  <TableRow key={agent.id}>
                    <TableCell><code>{agent.id}</code></TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{agent.name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>{agent.description}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={agent.agent_type || 'general'} size="small" sx={{ bgcolor: `${theme.palette.secondary.main}15`, color: theme.palette.secondary.main }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={brandName}
                        size="small"
                        sx={{ bgcolor: agent.brand_id > 0 ? `${theme.palette.info.main}15` : `${theme.palette.grey[400]}15`, color: agent.brand_id > 0 ? theme.palette.info.main : theme.palette.grey[600] }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${agent.tool_count} tools`}
                        size="small"
                        onClick={() => openToolAssignment(agent)}
                        sx={{ cursor: 'pointer', bgcolor: `${theme.palette.primary.main}15`, color: theme.palette.primary.main }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${agent.template_count || 0} templates`}
                        size="small"
                        onClick={() => openTemplateAssignment(agent)}
                        sx={{ cursor: 'pointer', bgcolor: `${theme.palette.warning.main}15`, color: theme.palette.warning.main }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={`${agent.user_count} users`} size="small" sx={{ bgcolor: `${theme.palette.success.main}15`, color: theme.palette.success.main }} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openAgentDialog(agent)} title="Edit"><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => openToolAssignment(agent)} title="Assign Tools" sx={{ color: theme.palette.primary.main }}><BuildIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => openTemplateAssignment(agent)} title="Assign Templates" sx={{ color: theme.palette.warning.main }}><DescriptionIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => confirmDelete('agent', agent.id, agent.name)} sx={{ color: theme.palette.error.main }} title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {agents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <SmartToyIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                      <Typography color="text.secondary">No agents yet. Create your first AI agent.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ---- Tools Sub-tab ---- */}
      {subTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Tools</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openToolDialog()} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)' }}>
              Add Tool
            </Button>
          </Box>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Approval</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Agents</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tools.map(tool => (
                  <TableRow key={tool.id}>
                    <TableCell><code>{tool.id}</code></TableCell>
                    <TableCell>{tool.name}</TableCell>
                    <TableCell>
                      <Chip label={tool.tool_type} size="small" sx={{ bgcolor: `${typeColor(tool.tool_type)}15`, color: typeColor(tool.tool_type) }} />
                    </TableCell>
                    <TableCell>
                      {tool.requires_approval ? (
                        <Chip label="Required" size="small" sx={{ bgcolor: `${theme.palette.warning.main}15`, color: theme.palette.warning.main }} />
                      ) : (
                        <Chip label="No" size="small" sx={{ bgcolor: `${theme.palette.grey[200]}`, color: theme.palette.grey[600] }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {(tool.agent_ids || []).length > 0
                        ? tool.agent_ids.map(aid => <Chip key={aid} label={aid} size="small" sx={{ mr: 0.5, mb: 0.5 }} />)
                        : <Typography variant="body2" color="text.secondary">None</Typography>
                      }
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openToolDialog(tool)} title="Edit"><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => confirmDelete('tool', tool.id, tool.name)} sx={{ color: theme.palette.error.main }} title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {tools.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <BuildIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                      <Typography color="text.secondary">No tools yet. Create your first tool.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ---- MCP Servers Sub-tab ---- */}
      {subTab === 2 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>Installed MCP Servers</Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Transport</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {servers.map(server => (
                  <TableRow key={server.id}>
                    <TableCell><code>{server.id}</code></TableCell>
                    <TableCell>{server.name}</TableCell>
                    <TableCell>
                      <Chip label={server.category || 'general'} size="small" />
                    </TableCell>
                    <TableCell>{server.transport}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!server.is_enabled}
                            onChange={() => handleToggleServer(server.id)}
                            color="primary"
                          />
                        }
                        label={server.is_enabled ? 'Enabled' : 'Disabled'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {servers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <StorageIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                      <Typography color="text.secondary">No MCP servers installed.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ---- Agent Create/Edit Dialog ---- */}
      <Dialog open={agentDialogOpen} onClose={() => setAgentDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{editingAgent ? 'Edit Agent' : 'Create Agent'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <TextField
            fullWidth label="Agent ID" value={agentForm.id}
            onChange={e => setAgentForm(f => ({ ...f, id: e.target.value }))}
            disabled={!!editingAgent} margin="normal" required
            helperText={!editingAgent ? 'Lowercase slug (e.g. "marketing-analyst")' : ''}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth label="Name" value={agentForm.name}
            onChange={e => setAgentForm(f => ({ ...f, name: e.target.value }))}
            margin="normal" required
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth label="Description" value={agentForm.description}
            onChange={e => setAgentForm(f => ({ ...f, description: e.target.value }))}
            margin="normal" multiline rows={2}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Autocomplete
              freeSolo
              options={[...new Set([...DEFAULT_AGENT_TYPES, ...agents.map(a => a.agent_type).filter(Boolean)])]}
              value={agentForm.agent_type}
              onInputChange={(e, value) => setAgentForm(f => ({ ...f, agent_type: value || 'general' }))}
              renderInput={(params) => (
                <TextField {...params} label="Agent Type" helperText="Select or type a custom type"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              )}
              sx={{ flex: 1 }}
            />
            <TextField
              select label="Brand Scope" value={agentForm.brand_id}
              onChange={e => setAgentForm(f => ({ ...f, brand_id: parseInt(e.target.value) || 0 }))}
              helperText="0 = all brands"
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value={0}>All Brands</MenuItem>
              {brands.filter(b => b.id !== 1).map(b => (
                <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
              ))}
            </TextField>
          </Box>
          <TextField
            fullWidth label="Personality Traits" value={agentForm.personality_traits}
            onChange={e => setAgentForm(f => ({ ...f, personality_traits: e.target.value }))}
            margin="normal" multiline rows={3}
            helperText="Role-based personality (e.g. 'You are a senior marketing analyst who focuses on ROAS optimization...'). Prepended to system prompt."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth label="System Prompt" value={agentForm.system_prompt}
            onChange={e => setAgentForm(f => ({ ...f, system_prompt: e.target.value }))}
            margin="normal" multiline rows={4}
            helperText="Instructions for the AI agent"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          {editingAgent && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Knowledge Documents</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload documents for this agent's knowledge base.
              </Typography>
              {agentForm.brand_id === 0 && (
                <TextField
                  select label="Upload to Brand" value={docBrandId}
                  onChange={e => setDocBrandId(parseInt(e.target.value) || 0)}
                  sx={{ mb: 2, width: '50%', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  size="small"
                >
                  <MenuItem value={0}><em>Select brand...</em></MenuItem>
                  {brands.filter(b => b.id !== 1).map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </TextField>
              )}
              {(agentForm.brand_id > 0 || docBrandId > 0) ? (
                <DocumentUploader
                  brandId={agentForm.brand_id > 0 ? agentForm.brand_id : docBrandId}
                  agentId={editingAgent.id}
                />
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Select a brand above to upload documents for this agent.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAgentDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAgent} disabled={loading} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)' }}>
            {loading ? <CircularProgress size={20} /> : (editingAgent ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Tool Assignment Dialog ---- */}
      <Dialog open={toolAssignDialogOpen} onClose={() => setToolAssignDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>Assign Tools to {assigningAgent?.name}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {tools.length === 0 ? (
            <Typography color="text.secondary">No tools available. Create tools first.</Typography>
          ) : (
            <FormGroup>
              {tools.map(tool => (
                <FormControlLabel
                  key={tool.id}
                  control={
                    <Checkbox
                      checked={agentToolIds.includes(tool.id)}
                      onChange={() => handleToggleToolAssignment(tool.id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{tool.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{tool.id} ({tool.tool_type})</Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setToolAssignDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveToolAssignment} disabled={loading} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)' }}>
            {loading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Template Assignment Dialog ---- */}
      <Dialog open={templateAssignDialogOpen} onClose={() => setTemplateAssignDialogOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>
          Assign Templates to {templateAssigningAgent?.name}
          <Typography variant="body2" color="text.secondary">
            {agentTemplateIds.length} template{agentTemplateIds.length !== 1 ? 's' : ''} selected
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <TextField
            fullWidth placeholder="Search templates..." value={templateSearch}
            onChange={e => setTemplateSearch(e.target.value)}
            size="small" sx={{ mb: 2, mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            }}
          />
          {allTemplates.length === 0 ? (
            <Typography color="text.secondary">No templates available.</Typography>
          ) : (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <FormGroup>
                {allTemplates
                  .filter(t => {
                    if (!templateSearch) return true;
                    const s = templateSearch.toLowerCase();
                    return (t.id || '').toLowerCase().includes(s) ||
                           (t.name || '').toLowerCase().includes(s) ||
                           (t.description || '').toLowerCase().includes(s);
                  })
                  .map(tpl => (
                    <FormControlLabel
                      key={tpl.id}
                      control={
                        <Checkbox
                          checked={agentTemplateIds.includes(tpl.id)}
                          onChange={() => handleToggleTemplateAssignment(tpl.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{tpl.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tpl.id} ({tpl.template_type})
                            {tpl.description ? ` - ${tpl.description.substring(0, 80)}${tpl.description.length > 80 ? '...' : ''}` : ''}
                          </Typography>
                        </Box>
                      }
                    />
                  ))
                }
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setTemplateAssignDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTemplateAssignment} disabled={loading} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)' }}>
            {loading ? <CircularProgress size={20} /> : `Save (${agentTemplateIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Tool Create/Edit Dialog ---- */}
      <Dialog open={toolDialogOpen} onClose={() => setToolDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{editingTool ? 'Edit Tool' : 'Create Tool'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <TextField
            fullWidth label="Tool ID" value={toolForm.id}
            onChange={e => setToolForm(f => ({ ...f, id: e.target.value }))}
            disabled={!!editingTool} margin="normal" required
            helperText={!editingTool ? 'Lowercase slug (e.g. "run-revenue-report")' : ''}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth label="Name" value={toolForm.name}
            onChange={e => setToolForm(f => ({ ...f, name: e.target.value }))}
            margin="normal" required
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth label="Description" value={toolForm.description}
            onChange={e => setToolForm(f => ({ ...f, description: e.target.value }))}
            margin="normal" multiline rows={2}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth select label="Tool Type" value={toolForm.tool_type}
            onChange={e => setToolForm(f => ({ ...f, tool_type: e.target.value }))}
            margin="normal" required
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          >
            {TOOL_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField
            fullWidth label="Config (JSON)" value={toolForm.config}
            onChange={e => setToolForm(f => ({ ...f, config: e.target.value }))}
            margin="normal" multiline rows={4}
            helperText="JSON configuration for the tool executor"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: 'monospace' } }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={toolForm.requires_approval}
                onChange={e => setToolForm(f => ({ ...f, requires_approval: e.target.checked }))}
              />
            }
            label="Requires admin approval before execution"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setToolDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTool} disabled={loading} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)' }}>
            {loading ? <CircularProgress size={20} /> : (editingTool ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Delete Confirmation Dialog ---- */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Typography>
            Are you sure you want to delete {deleteTarget?.type} <strong>{deleteTarget?.name}</strong> ({deleteTarget?.id})?
            {deleteTarget?.type === 'agent' && ' This will also remove all tool assignments and user assignments for this agent.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={loading} sx={{ borderRadius: 2 }}>
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIAgentsTab;
